import Vue from 'vue';
import { defineStore } from 'pinia';
import AttributeUtils from "../utility/attributeUtils";

export const useManageAttributes = defineStore('manageAttributes', {
  state: () => ({
    form: {
      attributesList: [],
      attributes: {},
      attributesJson: null,
      messagesList: [],
      template: null,
      jsonEditorError: false,
    },

    pu_form: {
      id: null,
      name: null,
      description: null,
      isActive: null,
      attributes: {
        message: null,
        messageType: null,
        type: null,
        gcma: null
      },
      instanceId: null,
    },

    statistics: {
      totalPersons: 0,
      blockedPersons: 0,
      messagesDelivered: 0,
      optedOutPersons: 0
    },

    originalData: {},

    templates: [],

    // Used for tracking document in editing
    documentId: null,

    // S = Simple. J = JSON Editor
    mode: 'S',

    defaultDataType: 'String',

    dataTypes: [
      {
        type: "String",
        disabled: false,
      },
      {
        type: "Number",
        disabled: false,
      },
      {
        type: "Boolean",
        disabled: false,
      },
      {
        type: "Advanced",
        disabled: false,
      },
      {
        type: "File",
        disabled: false,
      },
      {
        type: "Image",
        disabled: false,
      },
      {
        type: "Text",
        disabled: true,
      },
    ],

    errors: {
      name: false,
      description: false,
      status: false,
      type: false,
      message: false,
      attributes: false,
    },

    states: {
      disableTemplateFieldChanges: true,
      saving: false,
      saving_pu_form: false,
      removing_pu_form: false,
      adding_pu_form: false,
      change_message_status: false,
      fetching: false,
      removing: false,
      fetchingTemplate: false,
      showSuccess: false,
      removeSuccess: false,
      addedSuccess: false,
      showMessageTemplateSuccess: false,
      showRemoveMessageTemplateSuccess: false,
      showAddedMessageTemplateSuccess: false,
      showErrors: false,
      showPopUpErrors: false,
      invalid: false, // used when accessing a component with invalid id
      showJsonEditor: true,
      showAttributeValues: true,
      allowHTMLEditor: false,
      allowBase64EncDec: false,
      fetchTemplateFailed: false,
      approvalProcessEnabled: false,
      disableUploads: false,
    },

    errorMessage: null,

    headers: {
      "ETag": null
    },

    pu_mt_headers: {
      "ETag": null
    },

    permissions: [],
  }),
  getters: {
    getForm() {
      return this.form;
    },
    disableForm() {
      return this.states.saving ||
        this.states.saving_pu_form ||
        this.states.removing_pu_form ||
        this.states.adding_pu_form ||
        this.states.fetching ||
        this.states.removing ||
        this.states.fetchingTemplate ||
        this.states.change_message_status ||
        this.states.invalid;
    },
    disableCancel() {
      return (this.states.create || this.states.edit || this.states.change_message_status) && this.states.saving;
    },
    hasOriginalDataChanged() {
      return AttributeUtils.hasOriginalDataChanged(this.originalData.attributes, this.form.attributes);
    },
    // originalData(){
    //   return this.originalData;
    // },
    hasErrors() {
      let hasError = false;

      Object.keys(this.errors).forEach((key) => {
        if (this.errors[key]) {
          hasError = true;
        }
      });

      if (!hasError) {
        if (this.mode != "S") {
          hasError = this.form.jsonEditorError;
        }
      }

      if (!hasError) {
        this.form.attributesList.forEach(
          (attr) => {
            if ((attr.dataType == 'File' || attr.dataType == 'Image') && this.states.disableUploads) {
              return;
            }

            if (attr.hasError || attr.isDuplicate || attr.hasNameError) {
              hasError = true;
            }
          }
        );
      }

      return hasError;
    }
  },
  actions: {
    initializeStatistics(payload) {
      if (payload.attributes && payload.attributes.statistics) {
        Object.assign(this.statistics, payload.attributes.statistics);
      }
    },
    initializePopUp(payload) {
      Vue.set(this.pu_form, 'id', payload.result.id);
      Vue.set(this.pu_form, 'name', payload.result.name);
      Vue.set(this.pu_form, 'description', payload.result.description);
      Vue.set(this.pu_form, 'isActive', payload.result.isActive);
      Vue.set(this.pu_form, 'message', payload.result.attributes.message);
      Vue.set(this.pu_form, 'messageType', payload.result.attributes.messageType);
      Vue.set(this.pu_form, 'gcma', payload.result.attributes.gcma);
      Vue.set(this.pu_form, 'instanceId', payload.result.instanceId);
    },
    initialize(payload) {
      let result = payload.result;
      Object.keys(result).forEach(key => {
        Vue.set(this.form, key, result[key]);
      });
      if (result.attributes) {
        Vue.set(this.form, 'messageEngine', result.attributes.messageEngine);
        Vue.set(this.form, 'originationNumber', result.attributes.originationNumber);
        if (result.resourceType == 'messageTemplate') {
          Vue.set(this.form, 'message', result.attributes.message);
          Vue.delete(this.form.attributes, 'message');
          Vue.set(this.form, 'gcma', result.attributes.gcma);
          Vue.delete(this.form.attributes, 'gcma');
          Vue.set(this.form, 'reason', result.attributes.reason);
          Vue.delete(this.form.attributes, 'reason');
          Vue.set(this.form, 'type', result.attributes.type);
          Vue.delete(this.form.attributes, 'type');
          Vue.set(this.form, 'messageType', result.attributes.messageType == null ? 'sms' : result.attributes.messageType);
          Vue.delete(this.form.attributes, 'messageType');
        }

        let attrList;
        if (result.attributes.assets && this.form.messageType == 'mms') {
          attrList = AttributeUtils.convertAttributesToList(result.attributes.assets, false);
        } else {
          attrList = AttributeUtils.convertAttributesToList(result.attributes, false);
        }

        if (payload.disableDelete) {
          attrList.forEach(attr => (attr.disableDelete = true));
        }

        if (payload.disableUpdate) {
          attrList.forEach(attr => (attr.disableUpdate = true));
        }
        this.form.attributesList = attrList;

        if (result.attributes.message) {
          this.form.messagesList = [];
          Vue.set(this.form, 'messageProperties', result.attributes.messageProperties);
          if (result.attributes.message.hasOwnProperty('settings')) {
            Vue.set(this.form, 'countryCode', result.attributes.message.settings.default.countryCode);
            Vue.set(this.form, 'originationNumber', result.attributes.message.settings.default.originationNumber);

            const autoReplies = result.attributes.message.settings.default.autoReplies || {};
            for (var key of Object.keys(autoReplies)) {
              // Exclude keywords: 'DEFAULT' and those connected to actions
              const actionIds = result.attributes.message.settings.default.autoReplies[key].actionIds || [];
              if ((!actionIds.length) && (key != 'DEFAULT')) {
                var obj = {
                  'keyword': key,
                  'messageTemplateId': result.attributes.message.settings.default.autoReplies[key].messageTemplateId
                };
                this.form.messagesList.push(obj);
              }
            }
          }
        }
      }
      this.originalData = _.cloneDeep(result);
    },
    setAttributeDataType(payload) {
      this.form.attributesList.forEach((item) => {
        if (item.name == payload.name) {
          item.dataType = payload.dataType;
        }
      });
    },
    initializeTemplateAttributes(template) {
      this.originalData.attributes = _.cloneDeep(template.template);
    },
    setTemplates(templates) {
      this.templates = templates;
    },
    setPermissions(val) {
      _.merge(this.permissions, val);
    },
    setDocumentId(documentId) {
      this.documentId = documentId;
    },
    setDefaultDataType(dataType) {
      this.defaultDataType = dataType;
    },
    updateErrors(payload) {
      Object.keys(payload).forEach(key => {
        Vue.set(this.errors, key, payload[key]);
      });
    },
    updateStates(payload) {
      _.merge(this.states, payload);
    },
    updateHeaders(payload) {
      _.merge(this.headers, payload);
    },
    updateMessageTemplatePopUpHeaders(payload) {
      _.merge(this.pu_mt_headers, payload);
    },
    setErrorMessage(errorMessage) {
      this.errorMessage = errorMessage;
    },
    setDataTypes(dataTypes) {
      this.dataTypes = dataTypes;
    },
    updateValue(payload) {
      this.form[payload.key] = payload.value;
    },
    updateMessageTemplate(payload) {
      Vue.set(this.form.attributes, payload.key, payload.value);
    },
    updateCampaignTemplate(payload) {
      this.form.attributes[payload.key] = payload.value;
    },
    updateMode(mode) {
      this.mode = mode;
    },
    updateTemplate(template) {
      if (!template) {
        this.form.attributes = {};
        this.form.attributesList = [];
        this.form.template = template;
      } else if (!this.form.template || (this.form.template && this.form.template.id != template.id)) {
        this.form.template = template;
        if (template) {
          // _.merge(this.form.attributes, template.template || template.attributes);
          if (template.template && typeof template.template == 'object' && !Array.isArray(template.template)) {
            this.form.attributes = template.template;
          } else if (template.attributes && typeof template.attributes == 'object' && !Array.isArray(template.attributes)) {
            this.form.attributes = template.attributes;
          }

          this.form.attributesList = AttributeUtils.convertAttributesToList(
            this.form.attributes, this.states.disableTemplateFieldChanges,
            Object.keys(template.template || template.attributes)
          );
        }
      }
    },
    setTemplate(template) {
      // To just configure template without resetting attributes.
      this.form.template = template;
      let keys = Object.keys(template.template || template.attributes);
      this.form.attributesList.forEach(attr => {
        attr.disableUpdate = keys.indexOf(attr.name) != -1 ? this.states.disableTemplateFieldChanges : false,
          attr.disableDelete = false;
      });
    },
    updateAttributes(attributes) {
      this.form.attributes = attributes;
      this.form.attributesList = AttributeUtils.convertAttributesToList(attributes, false);
    },
    addAttribute(payload) {
      this.form.attributesList.push({
        "name": "",
        "description": "",
        "dataType": this.defaultDataType,
        "value": this.defaultDataType == 'Image' ? { type: 'image', file_content: null } : '',
        "disableUpdate": false,
        "disableDelete": false,
        "hasError": this.defaultDataType == 'Image' ? true : false,
        "hasNameError": true,
        "isDuplicate": false,
      });

      // Not adding to attributes object as empty name cannot be added.
    },
    deleteAttribute(index) {
      let attribute = this.form.attributesList[index];
      this.form.attributesList.splice(index, 1);
      Vue.delete(this.form.attributes, attribute.name);
      if (this.form.attributes.assets) {
        Vue.delete(this.form.attributes.assets, attribute.name);
      }
    },
    updateAttribute(payload) {
      let attribute = this.form.attributesList[payload.index];
      attribute.name = payload.name;
      attribute.description = payload.description;
      attribute.dataType = payload.dataType;
      this.form.attributesList[payload.index] = attribute;
      this.form.attributes = AttributeUtils.convertAttributesToObject(this.form.attributesList);
    },
    updateAttributeValue(payload) {
      let attribute = this.form.attributesList[payload.index];
      payload.value = AttributeUtils.convertValue(attribute, payload.value);
      attribute.value = payload.value;
      attribute.hasError = false;
      Vue.set(this.form.attributes, attribute.name, payload.value);
    },
    updateAttributeName(payload) {
      let attribute = this.form.attributesList[payload.index];
      attribute.name = payload.name;
      attribute.hasNameError = payload.name.trim().length == 0;
      if (AttributeUtils.isDuplicate(this.form.attributesList, payload.name, payload.index)) {
        attribute.isDuplicate = true;
      } else {
        attribute.isDuplicate = false;
        this.form.attributes = AttributeUtils.convertAttributesToObject(this.form.attributesList);
      }
    },
    updateAttributeDataType(payload) {
      let attribute = this.form.attributesList[payload.index];
      attribute.dataType = payload.dataType;
      attribute.value = AttributeUtils.initAttributeValue(attribute.dataType, attribute.value);
      if (attribute.name != "") {
        Vue.set(this.form.attributes, attribute.name, attribute.value);
      }
    },
    toggleAttributeState(payload) {
      let attribute = this.form.attributesList.find(item => item.name == payload.name);
      if (attribute) {
        attribute.disableUpdate = payload.disableUpdate;
      }
    },
    updateAttributeError(payload) {
      let attribute = this.form.attributesList[payload.index];
      attribute.hasError = payload.error;
    },
    updateJsonEditorError(payload) {
      this.form.jsonEditorError = payload;
    },
    setApprovalProcessStatus(payload) {
      this.states.approvalProcessEnabled = Boolean(_.get(payload, 'attributes.messageTemplate.approvalProcessEnabled'))
    }
  }
})
